import axios from 'axios'
import qs from 'qs'

import { maxImageFileSize } from 'libs/constants'

let uniqueId = 0

export class FileSizeError extends Error {}
export class TotalFileSizeError extends Error {}
export class UploadError extends Error {
  constructor (attachmentId) {
    super()
    this.attachmentId = attachmentId
  }
}
export class FileTypeError extends Error {
  constructor (attachmentId) {
    super()
    this.attachmentId = attachmentId
  }
}

function isImageType (type) {
  const imageTypes = [
    'image/png', 'image/jpg', 'image/jpeg', 'image/gif',
    'png', 'jpg', 'jpeg', 'gif'
  ]
  if (imageTypes.indexOf(type) >= 0) return true
  if (type.indexOf('image') === 0) return true
  return false
}

class AttachmentUploader {
  constructor () {
    this.maxCombinedAttachmentsSize = 104857600 // 100Mb
    this.maxAttachmentSize = 52428800 // 50Mb
    this.maxImageSize = maxImageFileSize

    this.totalAttachmentSize = 0
    this.fileStore = {}
  }

  prepareFile (file) {
    if (isImageType(file.type)) return this._prepareImage(file)
    return this._prepareOtherFile(file)
  }

  _prepareImage (file) {
    const tokenEl = document.getElementById('csrftoken')
    const csrfToken = tokenEl && tokenEl.value
    const attachmentId = uniqueId++
    if (file.size && file.size > this.maxImageSize) {
      throw new FileSizeError()
    }

    let formData = new window.FormData()
    formData.append('image', file)
    formData.append('yip_csrf_token', csrfToken)

    this.fileStore[attachmentId] = this._uploadImageFile(attachmentId, formData)

    return {
      attachmentId,
      source: '',
      source_thumb: '',
      type: 'image',
      file_original_name: file.name,
      file_size: file.size,
      file_uuid: ''
    }
  }

  async _uploadImageFile (attachmentId, formData) {
    let response
    try {
      response = await axios({
        url: '/attachment/new',
        method: 'POST',
        data: formData,
        config: {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      })
    } catch (err) {
      throw new UploadError(attachmentId)
    }
    const data = response.data
    return {
      source: data.source,
      source_thumb: data.source_thumb
    }
  }

  _prepareOtherFile (file) {
    const attachmentId = uniqueId++
    if (file.size && file.size > this.maxAttachmentSize) {
      throw new FileSizeError()
    }

    if (this.totalAttachmentSize + (file.size || 0) > this.maxCombinedAttachmentsSize) {
      throw new TotalFileSizeError()
    }
    this.totalAttachmentSize += file.size || 0

    this.fileStore[attachmentId] = this._uploadFileToS3(file, attachmentId)
    return {
      attachmentId,
      source: '',
      source_thumb: '',
      type: file.name.split('.').pop(),
      file_original_name: file.name,
      file_size: file.size,
      file_uuid: ''
    }
  }

  waitForUpload ({ attachmentId }) {
    const promise = this.fileStore[attachmentId]
    this.fileStore[attachmentId] = undefined
    return promise
  }

  getFileType (file) {
    let fileType = file.type

    if (!fileType && /^.+\.(xls)$/.test(file.name)) {
      fileType = 'application/vnd.ms-excel'
    }

    if (!fileType && /^.+\.(csv)$/.test(file.name)) {
      fileType = 'text/csv'
    }

    return fileType
  }

  async _uploadFileToS3 (file, attachmentId) {
    let fileType = this.getFileType(file)

    const url = `/attachment/signedUrl?filename=${file.name}&content_type=${fileType}`
    try {
      var getSignedUrlResponse = await axios.get(url)
    } catch (err) {
      if (err.response.data === 'File extension is not allowed') {
        throw new FileTypeError()
      }
      console.error(err)
      throw new UploadError(attachmentId)
    }

    const fileData = getSignedUrlResponse.data
    const searchQuery = window.location.search
    const queryParameters = qs.parse(searchQuery, { ignoreQueryPrefix: true })
    try {
      await axios({
        url: fileData.url,
        method: 'PUT',
        data: file,
        headers: {
          'content-type': fileType + '; charset=utf-8',
          'x-amz-tagging': queryParameters['x-amz-tagging'],
          'X-Requested-With': null
        }
      })
    } catch (err) {
      console.error(err)
      throw new UploadError(attachmentId)
    }
    return fileData
  }
}

export default AttachmentUploader
